import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, FolderKanban, Users, Eye, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { CampaignRow } from "@/components/CampaignRow";
import { TemplateLibrary } from "@/components/TemplateLibrary";

export const hubUrl = (slug) => `${window.location.origin}/h/${slug}`;

const ProgramCard = ({ p }) => (
  <Link to={`/admin/programs/${p.id}`} data-testid={`program-card-${p.hub_slug}`}
    className="group rounded-lg border border-[#242C3D] bg-[#141A24]/70 p-5 hover:border-indigo-500/50 transition-colors block">
    <div className="flex items-start justify-between gap-3 mb-3">
      <span className="font-mono text-xs px-2.5 py-1 rounded-md bg-indigo-500/10 border border-indigo-500/30 text-indigo-300" data-testid="program-code">{p.code}</span>
      <button type="button" data-testid={`program-open-hub-${p.hub_slug}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`/h/${p.hub_slug}`, "_blank"); }} className="text-slate-500 hover:text-white" title="Open content hub"><ExternalLink className="w-4 h-4" /></button>
    </div>
    <h3 className="font-semibold text-slate-100 leading-snug">{p.name}</h3>
    <p className="text-xs text-slate-500 mt-1">{p.client_name || "—"}</p>
    <div className="flex gap-4 mt-4 font-mono text-xs text-slate-400">
      <span className="flex items-center gap-1.5"><FolderKanban className="w-3.5 h-3.5" />{p.page_count} pages</span>
      <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" />{p.visit_count}</span>
      <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{p.lead_count} leads</span>
    </div>
  </Link>
);

export default function Dashboard() {
  const [programs, setPrograms] = useState(null);
  const [loose, setLoose] = useState([]);
  const nav = useNavigate();
  const load = () => {
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/analytics/overview").then((r) => setLoose(r.data.campaigns.filter((c) => !c.program_id)));
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 sv-rise">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-indigo-400 mb-3">Build</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Campaigns</h1>
        </div>
        <button data-testid="program-create-button" onClick={() => nav("/admin/programs/new")}
          className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-indigo-500 hover:bg-indigo-400 font-medium transition-colors self-start">
          <Plus className="w-4 h-4" /> New campaign
        </button>
      </div>

      {programs && programs.length === 0 && (
        <div className="rounded-lg border border-dashed border-[#2f3a50] py-16 text-center mb-12" data-testid="programs-empty-state">
          <p className="text-slate-400 mb-3">No campaigns yet. A campaign groups a client's landing pages and has its own content hub.</p>
          <Link to="/admin/programs/new" className="text-indigo-400 hover:text-indigo-300 text-sm font-medium">Create your first campaign →</Link>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12 sv-rise sv-d1" data-testid="programs-grid">
        {(programs || []).map((p) => <ProgramCard key={p.id} p={p} />)}
      </div>

      <TemplateLibrary />

      {loose.length > 0 && (
        <div className="rounded-lg border border-[#242C3D] bg-[#141A24]/60 sv-rise sv-d2" data-testid="unassigned-pages">
          <div className="px-6 py-4 border-b border-[#242C3D]">
            <h2 className="font-display font-semibold text-lg">Landing pages not in a campaign</h2>
            <p className="text-xs text-slate-500 mt-1">Edit a page to assign it to a campaign.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="campaigns-table">
              <PagesHead />
              <tbody>{loose.map((c) => <CampaignRow key={c.id} c={c} onChanged={load} />)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export const PagesHead = () => (
  <thead>
    <tr className="text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">
      <th className="px-6 py-3 font-medium">Landing page</th>
      <th className="px-4 py-3 font-medium">Status</th>
      <th className="px-4 py-3 font-medium text-right">Visits</th>
      <th className="px-4 py-3 font-medium text-right">Leads</th>
      <th className="px-4 py-3 font-medium text-right">Conv.</th>
      <th className="px-4 py-3 font-medium text-right">Avg time</th>
      <th className="px-6 py-3 font-medium text-right">Actions</th>
    </tr>
  </thead>
);
